import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Calendar, MapPin, Clock, ChevronLeft, ChevronRight, Loader2, Filter } from 'lucide-react';
import { cn } from '../lib/cn';

interface Event {
  id: string;
  title: string;
  description: string | null;
  type: string;
  location: string | null;
  startTime: string;
  endTime: string | null;
  allDay: boolean;
  isPublic: boolean;
  registrationEnabled: boolean;
  registrationCount: number;
  registrationLimit: number | null;
}

interface EventsPageProps {
  orgSlug: string;
}

const eventTypes = [
  { value: '', label: 'All Events' },
  { value: 'service', label: 'Services' },
  { value: 'class', label: 'Classes' },
  { value: 'social', label: 'Social' },
  { value: 'holiday', label: 'Holidays' },
  { value: 'lifecycle', label: 'Lifecycle' },
];

const getTypeColor = (type: string) => {
  switch (type) {
    case 'service':
      return 'bg-purple-100 text-purple-700';
    case 'class':
      return 'bg-blue-100 text-blue-700';
    case 'social':
      return 'bg-green-100 text-green-700';
    case 'lifecycle':
      return 'bg-pink-100 text-pink-700';
    case 'holiday':
      return 'bg-yellow-100 text-yellow-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export function EventsPage({ orgSlug }: EventsPageProps) {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['public-events', orgSlug, page, typeFilter],
    queryFn: async () => {
      const now = new Date().toISOString();
      const params = new URLSearchParams({
        from: now,
        page: page.toString(),
        limit: '12',
      });
      if (typeFilter) params.append('type', typeFilter);

      const response = await api.get<Event[]>(`/events?${params}`);
      if (!response.success) throw new Error(response.error?.message);
      return {
        events: response.data || [],
        meta: (response as { meta?: { total: number; page: number; totalPages: number } }).meta,
      };
    },
  });

  const formatEventDate = (dateStr: string, allDay: boolean) => {
    const date = new Date(dateStr);
    if (allDay) {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatEventTime = (dateStr: string, endDateStr: string | null, allDay: boolean) => {
    if (allDay) return 'All Day';
    const start = new Date(dateStr);
    const timeStr = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (endDateStr) {
      const end = new Date(endDateStr);
      const endTimeStr = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      return `${timeStr} - ${endTimeStr}`;
    }
    return timeStr;
  };

  return (
    <div className="py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900">Upcoming Events</h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Join us for services, classes, celebrations, and community gatherings.
          </p>
        </div>

        {/* Filter */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {eventTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        )}

        {/* Events Grid */}
        {!isLoading && data?.events && data.events.length > 0 && (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {data.events.map((event) => (
                <div
                  key={event.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2.5 py-1 text-xs font-medium',
                          getTypeColor(event.type)
                        )}
                      >
                        {event.type}
                      </span>
                      {event.registrationEnabled && event.registrationLimit && (
                        <span className="text-xs text-gray-500">
                          {event.registrationCount}/{event.registrationLimit} registered
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{event.title}</h3>

                    {event.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                    )}

                    <div className="space-y-2 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatEventDate(event.startTime, event.allDay)}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        {formatEventTime(event.startTime, event.endTime, event.allDay)}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          {event.location}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {data.meta && data.meta.totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {data.meta.page} of {data.meta.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.meta.totalPages}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}

        {/* No Events */}
        {!isLoading && (!data?.events || data.events.length === 0) && (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No Upcoming Events</h3>
            <p className="text-gray-500 mt-2">Check back soon for new events.</p>
          </div>
        )}
      </div>
    </div>
  );
}
